export const PARTNER_INVENTORY_VIEW_MODES = Object.freeze({
  FLAT: "flat",
  GROUPED: "grouped",
});

export const DEALER_INVENTORY_VIEW_SETTINGS = Object.freeze({
  FLAT: "flat",
  GROUPED: "grouped",
  AUTO: "auto",
});

export const GROUPED_DEFAULT_THRESHOLDS = Object.freeze({
  makeModelCoverage: 0.85,
  needsReviewRate: 0.15,
});

export function shouldDefaultToGroupedInventory(
  vehicleIdentityHealth,
  thresholds = GROUPED_DEFAULT_THRESHOLDS
) {
  if (!vehicleIdentityHealth) return false;

  const coverage = vehicleIdentityHealth.coverage || vehicleIdentityHealth.vehicleIdentity || {};
  const makeModelCoverage =
    coverage.makeModelCoverage ??
    Math.min(coverage.makeCoverage ?? 0, coverage.modelCoverage ?? 0);

  const totalListings =
    vehicleIdentityHealth.totalListings ?? vehicleIdentityHealth.vehicleIdentity?.totalListings ?? 0;
  const needsReviewCount =
    vehicleIdentityHealth.needsReviewCount ??
    vehicleIdentityHealth.vehicleIdentity?.needsReviewCount ??
    0;
  const needsReviewRate = totalListings > 0 ? needsReviewCount / totalListings : 0;

  if (!totalListings) return false;

  return (
    makeModelCoverage >= thresholds.makeModelCoverage &&
    needsReviewRate <= thresholds.needsReviewRate
  );
}

export function resolvePartnerInventoryDefaultView({
  urlView = null,
  userPreference = null,
  dealerSetting = DEALER_INVENTORY_VIEW_SETTINGS.AUTO,
  groupedViewAvailable = false,
  groupedDefaultEnabled = false,
  rolloutStage = "stage_0_internal",
  vehicleIdentityHealth = null,
  rolloutDefaultView = null,
  thresholds = GROUPED_DEFAULT_THRESHOLDS,
} = {}) {
  if (rolloutDefaultView?.view) {
    return rolloutDefaultView;
  }

  const normalizedUrl = String(urlView || "")
    .trim()
    .toLowerCase();
  if (
    normalizedUrl === PARTNER_INVENTORY_VIEW_MODES.FLAT ||
    normalizedUrl === PARTNER_INVENTORY_VIEW_MODES.GROUPED
  ) {
    if (normalizedUrl === PARTNER_INVENTORY_VIEW_MODES.GROUPED && !groupedViewAvailable) {
      return { view: PARTNER_INVENTORY_VIEW_MODES.FLAT, reason: "grouped_disabled" };
    }
    return { view: normalizedUrl, reason: "url_param" };
  }

  const normalizedPref = String(userPreference || "")
    .trim()
    .toLowerCase();
  if (
    normalizedPref === PARTNER_INVENTORY_VIEW_MODES.FLAT ||
    normalizedPref === PARTNER_INVENTORY_VIEW_MODES.GROUPED
  ) {
    if (normalizedPref === PARTNER_INVENTORY_VIEW_MODES.GROUPED && !groupedViewAvailable) {
      return { view: PARTNER_INVENTORY_VIEW_MODES.FLAT, reason: "grouped_disabled" };
    }
    return { view: normalizedPref, reason: "user_preference" };
  }

  const normalizedDealerSetting = String(dealerSetting || DEALER_INVENTORY_VIEW_SETTINGS.AUTO)
    .trim()
    .toLowerCase();
  if (normalizedDealerSetting === DEALER_INVENTORY_VIEW_SETTINGS.FLAT) {
    return { view: PARTNER_INVENTORY_VIEW_MODES.FLAT, reason: "dealer_override_flat" };
  }
  if (normalizedDealerSetting === DEALER_INVENTORY_VIEW_SETTINGS.GROUPED) {
    if (!groupedViewAvailable) {
      return { view: PARTNER_INVENTORY_VIEW_MODES.FLAT, reason: "grouped_disabled" };
    }
    return { view: PARTNER_INVENTORY_VIEW_MODES.GROUPED, reason: "dealer_override_grouped" };
  }

  if (!groupedViewAvailable || !groupedDefaultEnabled) {
    return { view: PARTNER_INVENTORY_VIEW_MODES.FLAT, reason: "rollout_flat_default" };
  }

  const coverageEligible = shouldDefaultToGroupedInventory(vehicleIdentityHealth, thresholds);

  if (rolloutStage === "stage_4_grouped_default_global" && coverageEligible) {
    return { view: PARTNER_INVENTORY_VIEW_MODES.GROUPED, reason: "global_rollout_coverage" };
  }

  if (rolloutStage === "stage_3_grouped_default_per_dealer" && coverageEligible) {
    return { view: PARTNER_INVENTORY_VIEW_MODES.GROUPED, reason: "dealer_coverage_threshold" };
  }

  return { view: PARTNER_INVENTORY_VIEW_MODES.FLAT, reason: "coverage_below_threshold" };
}

export function parseInventoryViewFromParams(searchParams) {
  const sp =
    searchParams instanceof URLSearchParams
      ? searchParams
      : new URLSearchParams(searchParams || "");
  const view = sp.get("view");
  if (view === PARTNER_INVENTORY_VIEW_MODES.GROUPED) {
    return PARTNER_INVENTORY_VIEW_MODES.GROUPED;
  }
  if (view === PARTNER_INVENTORY_VIEW_MODES.FLAT) {
    return PARTNER_INVENTORY_VIEW_MODES.FLAT;
  }
  return null;
}

export function hasStrongVehicleIdentityCoverage(vehicleIdentityHealth) {
  return shouldDefaultToGroupedInventory(vehicleIdentityHealth);
}
